import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sun, Calendar, Clock, Sparkles, Sunrise } from "lucide-react";
import afterSchoolPark from "@/assets/after-school-park.webp";
import clubsSports from "@/assets/clubs-sports.webp";

const programCategories = [
  {
    title: "After School",
    description: "Engaging programs from 3:00pm to 5:30pm that blend learning with fun activities.",
    icon: <Clock className="w-8 h-8" />,
    color: "primary",
    image: afterSchoolPark,
    link: "/after-school",
  },
  {
    title: "Early Care",
    description: "Before-school care from 6:30am to 7:30am with homework help, arts and crafts, and guided play.",
    icon: <Sunrise className="w-8 h-8" />,
    color: "yellow",
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=500&h=300&fit=crop&auto=format&q=45&fm=webp",
    link: "/early-care",
  },
  {
    title: "Clubs",
    description: "Sports, arts & media, and science & tech clubs for hands-on adventures.",
    icon: <Calendar className="w-8 h-8" />,
    color: "accent",
    image: clubsSports,
    link: "/clubs",
  },
  {
    title: "Holiday Break Camps",
    description: "Special programs during school breaks to keep kids engaged and happy.",
    icon: <Sparkles className="w-8 h-8" />,
    color: "destructive",
    image: "https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=500&h=300&fit=crop&auto=format&q=45&fm=webp",
    link: "/holiday-camps",
  },
  {
    title: "Summer Camp",
    description: "Unforgettable summer experiences filled with exploration and friendship.",
    icon: <Sun className="w-8 h-8" />,
    color: "secondary",
    image: "https://images.unsplash.com/photo-1596464716127-f2a82984de30?w=500&h=300&fit=crop&auto=format&q=45&fm=webp",
    link: "/summer-camps",
  },
];

const Programs = () => {
  return (
    <section id="programs" className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-foreground mb-4">
            Our Programs
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From after-school enrichment to summer adventures, we have something for every child.
          </p>
        </div>

        {/* Program Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {programCategories.map((program, index) => (
            <ProgramCategoryCard key={program.title} {...program} index={index} />
          ))}
        </div>

        {/* CTA */}
        <div className="flex justify-center mt-12">
          <Link to="/programs">
            <Button size="lg" className="font-bold rounded-full px-8 text-base gap-2">
              All Programs <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

interface ProgramCategoryCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  image: string;
  link: string;
  index: number;
}

const ProgramCategoryCard = ({ title, description, icon, color, image, link, index }: ProgramCategoryCardProps) => {
  const colorClasses: Record<string, string> = {
    primary: "bg-primary text-primary-foreground",
    accent: "bg-accent text-accent-foreground",
    secondary: "bg-secondary text-secondary-foreground",
    destructive: "bg-destructive text-destructive-foreground",
    yellow: "bg-yellow-400 text-yellow-950",
  };

  return (
    <div
      className="group relative bg-card rounded-3xl overflow-hidden card-shadow hover:card-shadow-hover transition-all duration-300 hover:-translate-y-1 animate-fade-up"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={image}
          alt={`Kids enjoying MSB Camps ${title} program`}
          width={500}
          height={300}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className={`absolute top-4 left-4 w-14 h-14 rounded-2xl ${colorClasses[color]} flex items-center justify-center shadow-lg`} aria-hidden="true">
          {icon}
        </div>
      </div>


      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-foreground mb-2">{title}</h3>
        <p className="text-muted-foreground mb-4">{description}</p>
        <Link to={link} aria-label={`Learn more about ${title}`}>
          <Button variant="ghost" className="font-semibold p-0 h-auto text-primary hover:text-primary/80 gap-1">
            Learn more about {title} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default Programs;